import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Circle } from 'lucide-react';
import PageTransition from '../components/PageTransition';

const Roadmap = () => {
  const roadmapItems = [
    {
      quarter: 'Q1 2026',
      title: 'Foundation',
      status: 'completed',
      items: ['Platform development', 'Security audits', 'Beta launch', 'Token distribution']
    },
    {
      quarter: 'Q2 2026',
      title: 'Growth',
      status: 'in-progress',
      items: ['Public launch', 'Mobile app', 'Exchange listings', 'Community expansion']
    },
    {
      quarter: 'Q3 2026',
      title: 'Ecosystem',
      status: 'upcoming',
      items: ['Trading bot', 'Staking program', 'NFT integration', 'DeFi protocols']
    },
    {
      quarter: 'Q4 2026',
      title: 'Scale',
      status: 'upcoming',
      items: ['Dobix Pay launch', 'Debit card', 'Global expansion', 'Enterprise solutions']
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'in-progress': return 'text-[#FF6B00]';
      default: return 'text-gray-400';
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen text-white bg-[#0A0A0A] pt-20">

        {/* Hero */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/5 via-transparent to-transparent" />

          <div className="relative max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-8"
            >
              <img
                src="/dobix-logo.png"
                alt="Dobix"
                className="h-20 w-20 mx-auto"
              />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold mb-6 font-heading"
            >
              Our <span className="text-gradient">Roadmap</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-400 max-w-3xl mx-auto"
            >
              Follow our journey as we build the future of decentralized payments.
            </motion.p>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FF6B00] via-[#FF6B00]/50 to-[#333333] hidden md:block" />

              <div className="space-y-8">
                {roadmapItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Dot */}
                    <div className="absolute left-6 w-5 h-5 hidden md:block">
                      {item.status === 'completed' ? (
                        <CheckCircle2 className={`w-5 h-5 ${getStatusColor(item.status)}`} />
                      ) : (
                        <Circle className={`w-5 h-5 ${getStatusColor(item.status)}`} />
                      )}
                    </div>

                    <div className="md:ml-16 card-dark rounded-2xl p-6 hover:border-[#FF6B00]/30 transition-all">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <div>
                          <p className={`text-sm font-semibold ${getStatusColor(item.status)} mb-1`}>
                            {item.quarter}
                          </p>
                          <h3 className="text-2xl font-bold">{item.title}</h3>
                        </div>
                        <span className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize ${item.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          item.status === 'in-progress' ? 'bg-[#FF6B00]/20 text-[#FF6B00]' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                          {item.status === 'in-progress' ? 'In Progress' : item.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {item.items.map((subItem, i) => (
                          <div key={i} className="flex items-center gap-2 text-gray-300">
                            <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" />
                            {subItem}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 font-heading">
                Building Together
              </h2>
              <p className="text-gray-400 text-lg">
                Our roadmap evolves with our community. Join us and help shape the future of Dobix.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Roadmap;